import { create } from 'zustand'
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { Discussion, Reply, CreateReplyInput } from '../models/Discussion'
import { useAuthStore } from './authStore'

interface DiscussionState {
  discussions: Discussion[]
  replies: Record<string, Reply[]>
  loading: boolean
  error: string | null
  fetchDiscussions: () => Promise<void>
  fetchReplies: (discussionId: string) => Promise<void>
  addAdminReply: (input: CreateReplyInput) => Promise<void>
  deleteDiscussion: (id: string) => Promise<void>
  deleteReply: (discussionId: string, replyId: string) => Promise<void>
}

export const useDiscussionStore = create<DiscussionState>((set, get) => ({
  discussions: [],
  replies: {},
  loading: false,
  error: null,

  fetchDiscussions: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(
        collection(db, 'discussions'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      const discussions: Discussion[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          userId: data.userId,
          authorName: data.authorName,
          content: data.content,
          createdAt: (data.createdAt as Timestamp).toDate(),
          updatedAt: data.updatedAt
            ? (data.updatedAt as Timestamp).toDate()
            : undefined,
          likes: data.likes || 0,
          likedBy: data.likedBy || [],
          isAnonymous: data.isAnonymous ?? false,
        }
      })

      set({ discussions, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入討論失敗',
      })
    }
  },

  fetchReplies: async (discussionId: string) => {
    try {
      const q = query(
        collection(db, 'discussions', discussionId, 'replies'),
        orderBy('createdAt', 'asc')
      )
      const snapshot = await getDocs(q)

      const replies: Reply[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          userId: data.userId,
          adminId: data.adminId,
          authorName: data.authorName,
          content: data.content,
          createdAt: (data.createdAt as Timestamp).toDate(),
          isAdminReply: data.isAdminReply ?? false,
        }
      })

      set((state) => ({
        replies: {
          ...state.replies,
          [discussionId]: replies,
        },
      }))
    } catch (error) {
      console.error('Failed to fetch replies:', error)
    }
  },

  addAdminReply: async (input: CreateReplyInput) => {
    set({ loading: true, error: null })
    try {
      const admin = useAuthStore.getState().admin
      if (!admin) throw new Error('請先登入')

      await addDoc(
        collection(db, 'discussions', input.discussionId, 'replies'),
        {
          adminId: admin.id,
          authorName: admin.displayName,
          content: input.content,
          createdAt: serverTimestamp(),
          isAdminReply: true,
        }
      )

      await get().fetchReplies(input.discussionId)
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '回覆失敗',
      })
      throw error
    }
  },

  deleteDiscussion: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'discussions', id))
      await get().fetchDiscussions()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除討論失敗',
      })
      throw error
    }
  },

  deleteReply: async (discussionId: string, replyId: string) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'discussions', discussionId, 'replies', replyId))
      await get().fetchReplies(discussionId)
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除回覆失敗',
      })
      throw error
    }
  },
}))
